import { DecimalPipe } from '@angular/common';
import { AnalysisReferenceComponent } from '@shared/components/analysis-reference.component';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '@features/dashboard/services/dashboard.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  ActivityItem,
  BulkRunCounts,
} from '@features/dashboard/models/dashboard.model';
import {
  absoluteTime,
  relativeTime,
} from '@features/dashboard/utils/relative-time';

const PAGE_SIZE = 20;
/** Load the next page once the scroll position is this close to the end. */
const SCROLL_THRESHOLD_PX = 200;

/**
 * What the pipeline was working on. All singular so the state verb agrees —
 * "Geodaten" would need "laufen", every other stage "läuft".
 */
const STAGE_NOUNS: Record<string, string> = {
  geocut: 'Geodatenaufbereitung',
  analyzing: 'Analyse',
  planning: 'Planung',
  complete: 'Analyse',
  idle: 'Analyse',
};

const BULK_RUN_ACTIONS: Record<string, string> = {
  pending: 'Massenausführung in Warteschlange',
  running: 'Massenausführung läuft',
  completed: 'Massenausführung abgeschlossen',
  failed: 'Massenausführung fehlgeschlagen',
  cancelled: 'Massenausführung abgebrochen',
};

/** One segment of a bulk run's outcome bar. */
export interface RunSegment {
  label: string;
  count: number;
  percent: number;
  colorClass: string;
}

/**
 * Outcome breakdown of a bulk run as a single stacked bar. "Offen" covers
 * everything not yet resolved (queued or still running), so the segments always
 * add up to the run's total and a partial run reads as partial.
 */
export function runSegments(counts: BulkRunCounts): RunSegment[] {
  const total = counts.total || 1;
  const open = Math.max(
    0,
    counts.total - counts.succeeded - counts.failed - counts.skipped,
  );
  return [
    { label: 'erfolgreich', count: counts.succeeded, colorClass: 'bg-primary' },
    { label: 'fehlgeschlagen', count: counts.failed, colorClass: 'bg-destructive' },
    { label: 'übersprungen', count: counts.skipped, colorClass: 'bg-muted-foreground/40' },
    { label: 'offen', count: open, colorClass: 'bg-border' },
  ]
    .filter((s) => s.count > 0)
    .map((s) => ({ ...s, percent: (s.count / total) * 100 }));
}

@Component({
  selector: 'app-latest-activity',
  standalone: true,
  imports: [AnalysisReferenceComponent, DecimalPipe],
  templateUrl: './latest-activity.html',
  styleUrl: './latest-activity.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LatestActivityComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private router = inject(Router);

  private total = signal(0);
  private page = signal(0);

  items = signal<ActivityItem[]>([]);
  isLoading = signal(true);
  isLoadingMore = signal(false);
  loadError = signal(false);

  totalItems = this.total;
  hasMore = computed(() => this.items().length < this.total());

  async ngOnInit(): Promise<void> {
    await this.loadNextPage();
  }

  private async loadNextPage(): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id;
    if (!orgId || this.isLoadingMore()) {
      this.isLoading.set(false);
      return;
    }

    const nextPage = this.page() + 1;
    if (nextPage > 1) this.isLoadingMore.set(true);

    try {
      const { items, total } = await this.dashboardService.getActivity(
        orgId,
        nextPage,
        PAGE_SIZE,
      );
      this.items.update((current) => [...current, ...items]);
      this.total.set(total);
      this.page.set(nextPage);
    } catch (error) {
      console.error('[LatestActivity] Failed to load activity:', error);
      this.loadError.set(true);
    } finally {
      this.isLoading.set(false);
      this.isLoadingMore.set(false);
    }
  }

  /** Bound to the row list's `(scroll)` — loads the next page near the end. */
  onScroll(event: Event): void {
    if (!this.hasMore() || this.isLoadingMore()) return;
    const el = event.target as HTMLElement;
    const distanceToEnd = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceToEnd < SCROLL_THRESHOLD_PX) {
      this.loadNextPage();
    }
  }

  // ─── Labels ───────────────────────────────────────────────────────────────

  kindLabel(item: ActivityItem): string {
    return item.kind === 'bulkRun' ? 'Kampagne' : 'Analyse';
  }

  /** "Planung abgeschlossen" — the stage names what happened, the state how it ended. */
  action(item: ActivityItem): string {
    if (item.kind === 'bulkRun') {
      return BULK_RUN_ACTIONS[item.status ?? ''] ?? 'Massenausführung';
    }
    const noun = STAGE_NOUNS[item.technicalStage ?? ''] ?? 'Analyse';
    switch (item.technicalState) {
      case 'completed':
        return `${noun} abgeschlossen`;
      case 'processing':
        return `${noun} läuft`;
      case 'queued':
        return `${noun} in Warteschlange`;
      case 'failed':
        return `${noun} fehlgeschlagen`;
      default:
        return 'Analyse angelegt';
    }
  }

  isFailed(item: ActivityItem): boolean {
    return item.technicalState === 'failed' || item.status === 'failed';
  }

  runSegments = runSegments;
  relativeTime = relativeTime;
  absoluteTime = absoluteTime;

  // ─── Navigation ───────────────────────────────────────────────────────────

  open(item: ActivityItem): void {
    const slug = this.currentOrgStore.organization()?.slug;
    if (!slug) return;
    const segment = item.kind === 'bulkRun' ? 'campaigns' : 'analyses';
    this.router.navigate(['/organizations', slug, segment, item.id]);
  }
}
